// src/components/shell/BucketTransitionPanel.tsx
// Doctrine A7 (v1): a brief lateral slide on the modes container when the
// active bucket changes. Pairs with BucketSwitchToast to make a bucket
// switch read as a "context shift" rather than a within-bucket mode tap.
//
// Implementation notes:
//   - Single-layer slide-in keyed on `activeBucketId`. The outgoing panel
//     is not visibly slid off-left in v1; only the incoming bucket slides
//     in from the right by a small translate distance. This trade-off is
//     intentional — see style_doctrine.md A7 entry.
//   - Mode swaps within the same bucket do not change `activeBucketId`,
//     so the effect is a no-op for those.
//   - Initial mount does not animate (the previous-ref starts at the
//     current value).
//   - `@media (prefers-reduced-motion: reduce)` short-circuits the
//     keyframe; the wrapper still mounts but renders statically.
//   - `LeadWorkspaceShellModes` keeps a stable mount and key, so panel
//     state (lead selection, training tab, ads intent, etc.) survives
//     the bucket switch as it did before this slice.
import { useEffect, useRef } from "react";
import type { BucketId } from "@/components/shell/modes/bucketsRegistry";

const ANIMATION_CLASS = "bucket-slide-active";
const ANIMATION_KEYFRAME = "bucket-slide-in";

export type BucketTransitionPanelProps = {
  activeBucketId: BucketId | null;
  children: React.ReactNode;
};

export function BucketTransitionPanel({ activeBucketId, children }: BucketTransitionPanelProps) {
  const innerRef = useRef<HTMLDivElement | null>(null);
  const prevBucketIdRef = useRef<BucketId | null>(activeBucketId);

  useEffect(() => {
    const prev = prevBucketIdRef.current;
    prevBucketIdRef.current = activeBucketId;

    if (prev === null || activeBucketId === null) return;
    if (prev === activeBucketId) return;

    const node = innerRef.current;
    if (!node) return;

    node.classList.remove(ANIMATION_CLASS);
    void node.offsetWidth;
    node.classList.add(ANIMATION_CLASS);

    const onEnd = () => {
      node.classList.remove(ANIMATION_CLASS);
      node.removeEventListener("animationend", onEnd);
    };
    node.addEventListener("animationend", onEnd);
    return () => {
      node.removeEventListener("animationend", onEnd);
    };
  }, [activeBucketId]);

  return (
    <>
      <style>{`
        @keyframes ${ANIMATION_KEYFRAME} {
          0%   { transform: translateX(28px); opacity: 0.6; }
          100% { transform: translateX(0);    opacity: 1; }
        }
        .${ANIMATION_CLASS} {
          animation: ${ANIMATION_KEYFRAME} 250ms ease-out both;
          will-change: transform, opacity;
        }
        @media (prefers-reduced-motion: reduce) {
          .${ANIMATION_CLASS} {
            animation: none;
          }
        }
      `}</style>
      <div ref={innerRef}>{children}</div>
    </>
  );
}
